import { supabase } from "@/integrations/supabase/client";

type Source = "tenant" | "owner" | "employee" | "service_provider";
type CheckResult = { found: boolean; source?: Source; company_id?: string };

/**
 * Throws a bilingual Error if the phone is already used by a tenant/owner/
 * employee under a DIFFERENT company. Caller is responsible for catching and
 * surfacing the error to the user (toast / form error).
 *
 * Per spec, the error message does NOT name the offending company.
 */
export async function assertPhoneAvailableAcrossCompanies(
  phone: string | null | undefined,
  excludeCompanyId: string,
  language: "ar" | "en" = "ar",
): Promise<void> {
  if (!phone) return;
  const { data, error } = await supabase.rpc("check_phone_in_other_companies", {
    p_phone: phone,
    p_exclude_company_id: excludeCompanyId,
  });
  if (error) throw error;
  const result = (data ?? { found: false }) as CheckResult;
  if (result.found) {
    const ar = "رقم الجوال مستخدم بالفعل في شركة أخرى";
    const en = "This phone number is already registered with another company";
    throw new Error(language === "ar" ? ar : en);
  }
}

export async function assertNationalIdAvailableAcrossCompanies(
  idNumber: string | null | undefined,
  excludeCompanyId: string,
  language: "ar" | "en" = "ar",
): Promise<void> {
  if (!idNumber) return;
  const { data, error } = await supabase.rpc("check_national_id_in_other_companies", {
    p_id_number: idNumber,
    p_exclude_company_id: excludeCompanyId,
  });
  if (error) throw error;
  const result = (data ?? { found: false }) as CheckResult;
  if (result.found) {
    const ar = "رقم الهوية مستخدم بالفعل في شركة أخرى";
    const en = "This national ID is already registered with another company";
    throw new Error(language === "ar" ? ar : en);
  }
}

export async function assertEmailAvailableAcrossCompanies(
  email: string | null | undefined,
  excludeCompanyId: string,
  language: "ar" | "en" = "ar",
): Promise<void> {
  if (!email || !email.includes("@")) return;
  const { data, error } = await supabase.rpc("check_email_in_other_companies", {
    p_email: email,
    p_exclude_company_id: excludeCompanyId,
  });
  if (error) throw error;
  const result = (data ?? { found: false }) as CheckResult;
  if (result.found) {
    const ar = "البريد الإلكتروني مستخدم بالفعل في شركة أخرى";
    const en = "This email is already registered with another company";
    throw new Error(language === "ar" ? ar : en);
  }
}
